import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Check, Key, Trash2, Plus, ExternalLink, AlertTriangle, Lock, ChevronDown, ChevronUp, TrendingUp, BarChart2, Calendar, PieChart, List, Activity, Brain, Award, Search, BookOpen, Grid, Zap, Radio } from 'lucide-react';
import { mcpApi, McpKeyItem, McpKeyCreated } from '../services/mcpApi';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Link } from 'react-router-dom';

// ---------------------------------------------------------------------------
// MCP Tool list (inline guide)
// ---------------------------------------------------------------------------
const MCP_TOOLS = [
  { name: 'get_market_data',        desc: 'Real-time quote for any symbol',           icon: TrendingUp },
  { name: 'get_historical_data',    desc: 'OHLCV bars — daily, weekly, monthly',      icon: BarChart2 },
  { name: 'get_economic_calendar',  desc: 'Upcoming macro events & impact ratings',   icon: Calendar },
  { name: 'get_portfolio_summary',  desc: 'Current portfolio value & P&L snapshot',   icon: PieChart },
  { name: 'list_portfolio_holdings', desc: 'All positions with entry price & P&L',    icon: List },
  { name: 'list_trades',            desc: 'Trade history with outcomes',              icon: Activity },
  { name: 'list_trading_analyses',  desc: 'AI signal history for any symbol',         icon: Brain },
  { name: 'get_trading_performance', desc: 'Win rate, drawdown, profit factor',       icon: Award },
  { name: 'search_strategies',      desc: 'Query your saved trading strategies',      icon: Search },
  { name: 'list_strategies',        desc: 'All strategies with rules & parameters',   icon: BookOpen },
  { name: 'get_market_heatmap',     desc: 'Sector performance across JSE & US',       icon: Grid },
  { name: 'get_hot_movers',         desc: 'Top gainers & losers right now',           icon: Zap },
  { name: 'list_signals',           desc: 'Latest AI-generated trading signals',      icon: Radio },
];

const MCP_SSE_URL = `${window.location.origin}/mcp/sse`;

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
    >
      {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function ConfigSnippet({ apiKey }: { apiKey: string }) {
  const [activeTab, setActiveTab] = useState<'claude' | 'cursor' | 'claude-code'>('claude');

  const snippet = JSON.stringify({
    mcpServers: {
      'my-investments': {
        url: MCP_SSE_URL,
        headers: { Authorization: `Bearer ${apiKey}` },
      },
    },
  }, null, 2);

  const tabs = [
    { id: 'claude' as const, label: 'Claude Desktop' },
    { id: 'cursor' as const, label: 'Cursor / Windsurf' },
    { id: 'claude-code' as const, label: 'Claude Code' },
  ];

  return (
    <div className="mt-6">
      <p className="text-sm font-medium text-slate-300 mb-3">Setup guide</p>
      <div className="flex gap-1 mb-3">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
              activeTab === t.id
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="relative bg-slate-900 border border-slate-700 rounded-lg p-4">
        <div className="absolute top-3 right-3">
          <CopyButton text={snippet} />
        </div>
        <pre className="text-xs text-slate-300 overflow-x-auto pr-16 whitespace-pre-wrap">{snippet}</pre>
        <p className="text-xs text-slate-500 mt-3">
          {activeTab === 'claude' && 'Paste into claude_desktop_config.json in your Claude Desktop config directory.'}
          {activeTab === 'cursor' && 'Paste into .cursor/mcp.json or .windsurf/mcp.json in your project root, or via the app\'s MCP settings.'}
          {activeTab === 'claude-code' && 'Paste into .claude/mcp.json or run /mcp add in the Claude Code CLI.'}
        </p>
      </div>
    </div>
  );
}

function NewKeyModal({
  keyData,
  onClose,
}: {
  keyData: McpKeyCreated;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Key size={18} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">API key generated</h3>
            <p className="text-xs text-slate-400">Copy this key now — it won't be shown again.</p>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between gap-2">
            <code className="text-sm text-cyan-300 font-mono break-all">{keyData.key}</code>
            <CopyButton text={keyData.key} />
          </div>
        </div>

        <ConfigSnippet apiKey={keyData.key} />

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

const McpSettings: React.FC = () => {
  const { isPro } = useSubscription();
  const qc = useQueryClient();
  const [labelInput, setLabelInput] = useState('');
  const [createdKey, setCreatedKey] = useState<McpKeyCreated | null>(null);
  const [revokeConfirm, setRevokeConfirm] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const { data: keys, isLoading } = useQuery<McpKeyItem[]>({
    queryKey: ['mcpKeys'],
    queryFn: () => mcpApi.listKeys(),
    enabled: isPro,
  });

  const generateMutation = useMutation({
    mutationFn: () => mcpApi.generateKey(labelInput || undefined),
    onSuccess: (data) => {
      setCreatedKey(data);
      setLabelInput('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['mcpKeys'] });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: number) => mcpApi.revokeKey(keyId),
    onSuccess: () => {
      setRevokeConfirm(null);
      qc.invalidateQueries({ queryKey: ['mcpKeys'] });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  if (!isPro) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
            <Lock size={24} className="text-amber-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">API Access</h2>
          <p className="text-slate-400 mb-4 text-sm">
            Connect AI coding tools (Cursor, Windsurf, Claude Code) and AI assistants (Claude Desktop, ChatGPT, OpenAI agents) directly to your my investments portfolio data.
            Available on Pro and Premium plans.
          </p>
          <Link
            to="/billing"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Upgrade to Pro
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">API Access</h1>
        <p className="text-slate-400 text-sm">
          Generate API keys to connect AI coding tools (Cursor, Windsurf, Claude Code) and AI assistants (Claude Desktop, ChatGPT, OpenAI agents) directly to your my investments portfolio.{' '}
          <button
            onClick={() => setGuideOpen(!guideOpen)}
            className="text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
          >
            {guideOpen ? 'Hide guide' : 'Read the full guide'}
            {guideOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </p>
      </div>

      {/* Inline guide — expands in-place */}
      {guideOpen && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-6">
          <div>
            <h2 className="text-lg font-bold text-white mb-2">13 MCP Tools</h2>
            <p className="text-slate-400 text-xs mb-4">All 13 tools are available to Pro and Premium subscribers equally.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {MCP_TOOLS.map((tool) => (
                <div key={tool.name} className="flex items-start gap-2 text-xs">
                  <tool.icon size={14} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <code className="text-slate-200">{tool.name}</code>
                    <p className="text-slate-500 mt-0.5">{tool.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-700 pt-4">
            <h3 className="text-sm font-semibold text-white mb-2">Quick Setup</h3>
            <p className="text-xs text-slate-400 mb-2">
              1. Generate an API key above<br />
              2. Copy the config snippet from the "Setup guide" section below your key<br />
              3. Paste into your AI tool's MCP config file<br />
              4. Restart the tool — your portfolio data is now available
            </p>
            <p className="text-xs text-slate-500">
              Supported tools: Claude Desktop, Cursor, Windsurf, Claude Code, ChatGPT (via plugin), OpenAI agents
            </p>
          </div>

          <div className="border-t border-slate-700 pt-4">
            <h3 className="text-sm font-semibold text-white mb-2">Plan access</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-slate-900/60 rounded-lg p-3">
                <p className="font-medium text-slate-500 mb-1">Free</p>
                <p className="text-slate-600">No MCP access</p>
              </div>
              <div className="bg-cyan-900/20 border border-cyan-600/30 rounded-lg p-3">
                <p className="font-medium text-cyan-400 mb-1">Pro & Premium</p>
                <p className="text-slate-400">All 13 tools, up to 5 API keys, full portfolio access</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate new key */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wider">Generate new key</h2>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Label (optional)"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            maxLength={80}
            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          />
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending || (keys?.length ?? 0) >= 5}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus size={14} />
            {generateMutation.isPending ? 'Generating…' : 'Generate'}
          </button>
        </div>
        {(keys?.length ?? 0) >= 5 && (
          <p className="text-xs text-amber-400 mt-2">Maximum 5 keys reached. Revoke an existing key to create a new one.</p>
        )}
        {error && (
          <div className="flex items-center gap-2 mt-3 text-xs text-red-400">
            <AlertTriangle size={13} />
            {error}
          </div>
        )}
      </div>

      {/* Key list */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wider">
          Active keys {!isLoading && keys && `(${keys.length}/5)`}
        </h2>

        {isLoading && (
          <p className="text-sm text-slate-500">Loading keys…</p>
        )}

        {!isLoading && (!keys || keys.length === 0) && (
          <p className="text-sm text-slate-500">No API keys yet. Generate one above to get started.</p>
        )}

        {keys && keys.length > 0 && (
          <div className="space-y-3">
            {keys.map((k) => (
              <div
                key={k.id}
                className="flex items-center justify-between bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-cyan-300 font-mono">{k.key_prefix}…</code>
                    {k.label && (
                      <span className="text-xs text-slate-400">{k.label}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Created {new Date(k.created_at).toLocaleDateString()}
                    {k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                  </p>
                </div>
                <div>
                  {revokeConfirm === k.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-400">Revoke?</span>
                      <button
                        onClick={() => revokeMutation.mutate(k.id)}
                        disabled={revokeMutation.isPending}
                        className="px-2 py-1 text-xs bg-red-600 hover:bg-red-500 text-white rounded"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setRevokeConfirm(null)}
                        className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setRevokeConfirm(k.id)}
                      className="p-1.5 text-slate-500 hover:text-red-400 rounded transition-colors"
                      title="Revoke key"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Server URL info */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-3 uppercase tracking-wider">Connection info</h2>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-slate-400 mb-1">MCP SSE endpoint</p>
            <div className="flex items-center gap-2">
              <code className="text-xs text-cyan-300 bg-slate-900 border border-slate-700 rounded px-2 py-1 flex-1">
                {MCP_SSE_URL}
              </code>
              <CopyButton text={MCP_SSE_URL} />
            </div>
          </div>
        </div>
      </div>

      {/* One-time key modal */}
      {createdKey && (
        <NewKeyModal keyData={createdKey} onClose={() => setCreatedKey(null)} />
      )}
    </div>
  );
};

export default McpSettings;
